import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import '../../css/admin.css';
import { applyAffiliateTag } from '../../utils/amazon.js';

const titleCache = new WeakMap();
const channelTitleCache = new WeakMap();

const MANIFEST_URL = '/script/cache/manifest.json';
const CACHE_BASE   = '/script/cache/';
const FLAGS_API    = '/api/admin-flags.php';
const GEAR_API     = '/api/admin-gear.php';

const FLAG_DEFS = [
  { key: 'isnaughty',                       icon: '🔞', label: 'Naughty',   cls: 'naughty',  activeVal: 1, inactiveVal: 0 },
  { key: 'notasmr',                         icon: '📵', label: 'Not ASMR', cls: 'notasmr',  activeVal: 1, inactiveVal: 0 },
  { key: 'status_embeddable',               icon: '🔒', label: 'No Embed', cls: 'embed',    activeVal: 0, inactiveVal: 1 },
  { key: 'status_madeforkids',              icon: '👶', label: 'Kids',      cls: 'kids',     activeVal: 1, inactiveVal: 0 },
  { key: 'status_selfdeclaredmadeforkids',  icon: '📢', label: 'Self Kids', cls: 'selfkids', activeVal: 1, inactiveVal: 0 },
];

const FLAG_DEFAULTS = {
  isnaughty: 0, notasmr: 0, status_embeddable: 1,
  status_madeforkids: 0, status_selfdeclaredmadeforkids: 0,
};

function apiFetch(token, method, body) {
  return fetch(FLAGS_API, {
    method,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
}

async function loadVideosFromCache() {
  const manifest = await fetch(MANIFEST_URL).then(r => r.json());
  if (!manifest?.version) throw new Error('Bad manifest');

  const version = manifest.version;
  const cats    = manifest.categories || [];
  const seen    = {};
  const allArr  = [];
  const byCat   = { all: [] };

  await Promise.all(cats.map(async catName => {
    try {
      const data = await fetch(`${CACHE_BASE}${version}/by-category/${catName}.json`).then(r => r.json());
      byCat[catName] = data.videos || [];
      (data.videos || []).forEach(v => {
        if (v.id && !seen[v.id]) { seen[v.id] = true; allArr.push(v); }
      });
    } catch { /* skip missing category */ }
  }));

  // Page-001 may contain videos not in any category
  try {
    const pg = await fetch(`${CACHE_BASE}${version}/pages/page-001.json`).then(r => r.json());
    (pg.videos || []).forEach(v => {
      if (v.id && !seen[v.id]) { seen[v.id] = true; allArr.push(v); }
    });
  } catch { /* optional */ }

  byCat.all = allArr;
  return { allVideos: allArr, videosByCategory: byCat, categories: cats };
}

function buildPageRange(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set    = new Set([1, total, cur, cur - 1, cur + 1].filter(p => p >= 1 && p <= total));
  const sorted = [...set].sort((a, b) => a - b);
  const out    = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

// ── Sub-components ──────────────────────────────────────────────

const EMPTY_FLAGS = {};

// ⚡ Bolt: Wrapped VideoCard in React.memo and stabilized callbacks to prevent O(N) re-renders
const VideoCard = React.memo(function VideoCard({ video, flags, blockedInfo, onFlagToggle, onChannelBlock }) {
  const id      = video.id || '';
  const isChannelBlocked = !!blockedInfo;
  const title   = video.snippet?.title || '(no title)';
  const chTitle = video.snippet?.channelTitle || video.snippet?.channeltitle || '';
  const chId    = video.snippet?.channelId    || video.snippet?.channelid    || '';
  const hasFlagSet = Object.keys(flags).length > 0;

  return (
    <div className={`ar-card${hasFlagSet ? ' ar-card-flagged' : ''}${isChannelBlocked ? ' ar-card-blocked' : ''}`}>
      <div
        className="ar-thumb"
        title="Open on YouTube"
        onClick={() => window.open(`https://www.youtube.com/watch?v=${id}`, '_blank', 'noopener')}
      >
        <img src={`https://i.ytimg.com/vi/${id}/mqdefault.jpg`} alt="" loading="lazy" />
        <div className="ar-thumb-play">▶</div>
        <div className="ar-thumb-badges">
          {flags.isnaughty                       === 1 && <span className="ar-badge ar-badge-naughty">🔞</span>}
          {flags.notasmr                         === 1 && <span className="ar-badge ar-badge-notasmr">📵</span>}
          {flags.status_embeddable               === 0 && <span className="ar-badge ar-badge-embed">🔒</span>}
          {flags.status_madeforkids              === 1 && <span className="ar-badge ar-badge-kids">👶</span>}
          {flags.status_selfdeclaredmadeforkids  === 1 && <span className="ar-badge ar-badge-selfkids">📢</span>}
          {isChannelBlocked && <span className="ar-badge ar-badge-blocked">CH</span>}
        </div>
      </div>

      <div className="ar-card-body">
        <div className="ar-card-title" title={title}>{title}</div>

        <div className="ar-card-channel">
          <span title={chTitle}>{chTitle}</span>
          <button
            className={`ar-btn-block-ch${isChannelBlocked ? ' ar-ch-blocked' : ''}`}
            title={isChannelBlocked ? 'Click to unblock this channel' : 'Block all videos from this channel'}
            onClick={() => onChannelBlock(chId, chTitle, blockedInfo)}
          >
            {isChannelBlocked ? 'Blocked' : 'Block ch.'}
          </button>
        </div>

        <div className="ar-flag-row">
          {FLAG_DEFS.map(def => {
            const isOn = flags[def.key] === def.activeVal;
            return (
              <button
                key={def.key}
                className={`ar-flag-btn${isOn ? ` ar-flag-on-${def.cls}` : ''}`}
                title={def.key}
                onClick={() => onFlagToggle(id, def, flags)}
              >
                <span className="ar-flag-icon">{def.icon}</span>
                <span className="ar-flag-label">{def.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
});

VideoCard.displayName = 'VideoCard';

VideoCard.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.string,
    snippet: PropTypes.shape({
      title: PropTypes.string,
      channelTitle: PropTypes.string,
      channeltitle: PropTypes.string,
      channelId: PropTypes.string,
      channelid: PropTypes.string,
    }),
  }).isRequired,
  flags: PropTypes.shape({
    isnaughty: PropTypes.number,
    notasmr: PropTypes.number,
    status_embeddable: PropTypes.number,
    status_madeforkids: PropTypes.number,
    status_selfdeclaredmadeforkids: PropTypes.number,
  }).isRequired,
  blockedInfo: PropTypes.shape({
    title: PropTypes.string,
    blocked_at: PropTypes.string,
  }),
  onFlagToggle: PropTypes.func.isRequired,
  onChannelBlock: PropTypes.func.isRequired,
};

function Pagination({ currentPage, totalPages, total, pageSize, onPageChange }) {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, total);

  return (
    <div className="ar-pagination">
      <span className="ar-page-info">{start}–{end} of {total.toLocaleString()}</span>
      <button className="ar-page-btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>← Prev</button>
      {buildPageRange(currentPage, totalPages).map((p, i) =>
        p === '…'
          ? <span key={`ell-${i}`} className="ar-page-ellipsis">…</span>
          : <button key={p} className={`ar-page-btn${p === currentPage ? ' ar-page-current' : ''}`} onClick={() => onPageChange(p)}>{p}</button>
      )}
      <button className="ar-page-btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>Next →</button>
    </div>
  );
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

function BlockedList({ blockedChannels, onUnblock }) {
  const entries = Object.entries(blockedChannels);
  if (entries.length === 0) return <div className="ar-blocked-empty">No channels blocked yet.</div>;
  return (
    <div className="ar-blocked-list">
      {entries.map(([chId, info]) => (
        <div key={chId} className="ar-blocked-row">
          <div style={{ flex: 1 }}>
            <div className="ar-blocked-name">{info.title || chId}</div>
            <div className="ar-blocked-date">{info.blocked_at || ''}</div>
          </div>
          <button className="ar-btn-unblock" onClick={() => onUnblock(chId, info.title || chId)}>Unblock</button>
        </div>
      ))}
    </div>
  );
}

BlockedList.propTypes = {
  blockedChannels: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string,
      blocked_at: PropTypes.string,
    })
  ).isRequired,
  onUnblock: PropTypes.func.isRequired,
};

// ── Gear manager ─────────────────────────────────────────────────

function gearApiFetch(token, method, body) {
  return fetch(GEAR_API, {
    method,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
}

const EMPTY_GEAR_FORM = { id: null, title: '', url: '', image: '', description: '', price: '' };

// Self-contained gear panel. Mounts only when the Gear tab is active, does its
// own loading via the admin token, and never touches the video review state.
function GearManager({ token, addToast }) {
  const [products,     setProducts]     = useState([]);
  const [affiliateTag, setAffiliateTag] = useState('');
  const [tagDraft,     setTagDraft]     = useState('');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [form,         setForm]         = useState(EMPTY_GEAR_FORM);
  const [saving,       setSaving]       = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    gearApiFetch(token, 'GET')
      .then(data => {
        setProducts(Array.isArray(data.products) ? data.products : []);
        const tag = typeof data.affiliate_tag === 'string' ? data.affiliate_tag : '';
        setAffiliateTag(tag);
        setTagDraft(tag);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const isEditing = form.id !== null;
  const setField  = key => e => { const { value } = e.target; setForm(f => ({ ...f, [key]: value })); };

  const submitForm = useCallback(async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const url   = form.url.trim();
    if (!title || !url) { addToast('Title and URL are required', 'error'); return; }

    setSaving(true);
    const product = {
      title, url,
      image:       form.image.trim(),
      description: form.description.trim(),
      price:       form.price.trim(),
    };
    try {
      if (isEditing) {
        await gearApiFetch(token, 'POST', { action: 'update_product', id: form.id, product });
        addToast('Product updated ✓');
      } else {
        await gearApiFetch(token, 'POST', { action: 'add_product', product });
        addToast('Product added ✓');
      }
      setForm(EMPTY_GEAR_FORM);
      reload();
    } catch (err) {
      addToast('Save failed: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }, [form, isEditing, token, addToast, reload]);

  const editProduct = useCallback((p) => {
    setForm({
      id: p.id,
      title:       p.title || '',
      url:         p.url || '',
      image:       p.image || '',
      description: p.description || '',
      price:       p.price || '',
    });
    window.scrollTo(0, 0);
  }, []);

  const deleteProduct = useCallback(async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await gearApiFetch(token, 'POST', { action: 'delete_product', id: p.id });
      addToast('Product deleted');
      setForm(prev => (prev.id === p.id ? EMPTY_GEAR_FORM : prev));
      reload();
    } catch (err) {
      addToast('Delete failed: ' + err.message, 'error');
    }
  }, [token, addToast, reload]);

  const saveTag = useCallback(async () => {
    const tag = tagDraft.trim();
    try {
      await gearApiFetch(token, 'POST', { action: 'set_affiliate_tag', affiliate_tag: tag });
      setAffiliateTag(tag);
      addToast(tag ? 'Affiliate tag saved ✓' : 'Affiliate tag cleared');
    } catch (err) {
      addToast('Save failed: ' + err.message, 'error');
    }
  }, [tagDraft, token, addToast]);

  const formUrl = form.url.trim();

  return (
    <div className="ar-gear">
      {/* Affiliate tag control */}
      <div className="ar-gear-section">
        <h3>Amazon Affiliate Tag</h3>
        <p className="ar-gear-help">
          Leave blank to serve plain public Amazon links. Enter your Associates tracking ID
          (e.g. <code>asmrguru-20</code>) and every product link — current and future — becomes
          an affiliate link automatically.
        </p>
        <div className="ar-gear-tagbar">
          <input
            className="ar-gear-input"
            type="text"
            placeholder="e.g. asmrguru-20"
            value={tagDraft}
            onChange={e => setTagDraft(e.target.value)}
          />
          <button className="ar-btn-primary ar-gear-tag-save" type="button" onClick={saveTag}>Save tag</button>
        </div>
        <div className={`ar-gear-tag-status${affiliateTag ? ' ar-gear-tag-on' : ''}`}>
          {affiliateTag
            ? <>Affiliate links active — using <strong>tag={affiliateTag}</strong></>
            : <>Plain public links (no affiliate tag set)</>}
        </div>
      </div>

      {/* Add / edit form */}
      <div className="ar-gear-section">
        <h3>{isEditing ? 'Edit product' : 'Add a product'}</h3>
        <form className="ar-gear-form" onSubmit={submitForm}>
          <label className="ar-gear-field">
            <span>Title *</span>
            <input className="ar-gear-input" value={form.title} onChange={setField('title')} placeholder="Blue Yeti USB Microphone" />
          </label>
          <label className="ar-gear-field">
            <span>Amazon URL *</span>
            <input className="ar-gear-input" value={form.url} onChange={setField('url')} placeholder="https://www.amazon.com/dp/…" />
          </label>
          <div className="ar-gear-field-row">
            <label className="ar-gear-field">
              <span>Price (optional)</span>
              <input className="ar-gear-input" value={form.price} onChange={setField('price')} placeholder="$99" />
            </label>
            <label className="ar-gear-field">
              <span>Image URL (optional)</span>
              <input className="ar-gear-input" value={form.image} onChange={setField('image')} placeholder="https://…jpg" />
            </label>
          </div>
          <label className="ar-gear-field">
            <span>Description (optional)</span>
            <textarea className="ar-gear-textarea" value={form.description} onChange={setField('description')} rows={2} placeholder="Why you recommend it" />
          </label>

          {formUrl !== '' && (
            <div className="ar-gear-preview">
              <span>Link visitors will get:</span>{' '}
              <a href={applyAffiliateTag(formUrl, affiliateTag)} target="_blank" rel="noopener noreferrer">
                {applyAffiliateTag(formUrl, affiliateTag)}
              </a>
            </div>
          )}

          <div className="ar-gear-form-actions">
            <button className="ar-btn-primary" type="submit" disabled={saving}>
              {isEditing ? 'Save changes' : 'Add product'}
            </button>
            {isEditing && (
              <button className="ar-btn-sm" type="button" onClick={() => setForm(EMPTY_GEAR_FORM)}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      {/* Product list */}
      <div className="ar-gear-section">
        <h3>Products ({products.length})</h3>
        {loading && <div className="ar-loading"><div className="ar-spinner" />Loading…</div>}
        {error   && <div className="ar-error">Failed to load: {error}</div>}
        {!loading && !error && products.length === 0 && (
          <div className="ar-gear-empty">No products yet. Add your first one above.</div>
        )}
        {!loading && !error && products.length > 0 && (
          <div className="ar-gear-list">
            {products.map(p => (
              <div key={p.id} className="ar-gear-row">
                {p.image
                  ? <img className="ar-gear-thumb" src={p.image} alt="" />
                  : <div className="ar-gear-thumb ar-gear-thumb--ph" aria-hidden="true">🎧</div>}
                <div className="ar-gear-row-info">
                  <div className="ar-gear-row-title">
                    {p.title}{p.price ? <span className="ar-gear-row-price"> · {p.price}</span> : null}
                  </div>
                  <a className="ar-gear-row-url" href={applyAffiliateTag(p.url, affiliateTag)} target="_blank" rel="noopener noreferrer">
                    {applyAffiliateTag(p.url, affiliateTag)}
                  </a>
                </div>
                <div className="ar-gear-actions">
                  <button className="ar-btn-sm" type="button" onClick={() => editProduct(p)}>Edit</button>
                  <button className="ar-btn-sm ar-gear-del" type="button" onClick={() => deleteProduct(p)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

GearManager.propTypes = {
  token: PropTypes.string.isRequired,
  addToast: PropTypes.func.isRequired,
};

// ── Main component ───────────────────────────────────────────────

export default function AdminReview() {
  // Auth
  const [token,      setToken]      = useState(() => sessionStorage.getItem('admin_token') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginPw,    setLoginPw]    = useState('');
  const [loginError, setLoginError] = useState(false);

  // Data
  const [allVideos,        setAllVideos]        = useState([]);
  const [videosByCategory, setVideosByCategory] = useState({ all: [] });
  const [categories,       setCategories]       = useState([]);
  const [flaggedVideos,    setFlaggedVideos]    = useState({});
  const [blockedChannels,  setBlockedChannels]  = useState({});

  // UI
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(48);
  const [showFilter,  setShowFilter]  = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeCat,   setActiveCat]   = useState('all');
  const [loading,     setLoading]     = useState(false);
  const [loadError,   setLoadError]   = useState(null);
  const [adminView,   setAdminView]   = useState('videos'); // 'videos' | 'gear'

  // Toasts
  const [toasts, setToasts] = useState([]);
  const toastRef = useRef(0);

  const addToast = useCallback((msg, type = 'success') => {
    const id = ++toastRef.current;
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2800);
  }, []);

  // Login
  const doLogin = useCallback(async (pw) => {
    if (!pw) return;
    try {
      const data = await apiFetch(pw, 'GET');
      sessionStorage.setItem('admin_token', pw);
      setToken(pw);
      setFlaggedVideos(data.flagged_videos   || {});
      setBlockedChannels(data.blocked_channels || {});
      setIsLoggedIn(true);
      setLoginError(false);
    } catch {
      setLoginError(true);
      sessionStorage.removeItem('admin_token');
      setToken('');
    }
  }, []);

  // Auto-login on mount if token was saved in sessionStorage.
  // Reads sessionStorage directly so doLogin (stable callback) is the only dep.
  useEffect(() => {
    const saved = sessionStorage.getItem('admin_token');
    if (saved) doLogin(saved);
  }, [doLogin]);

  // Load videos after successful login
  useEffect(() => {
    if (!isLoggedIn) return;
    setLoading(true);
    setLoadError(null);
    loadVideosFromCache()
      .then(({ allVideos, videosByCategory, categories }) => {
        setAllVideos(allVideos);
        setVideosByCategory(videosByCategory);
        setCategories(categories);
        setLoading(false);
      })
      .catch(e => { setLoadError(e.message); setLoading(false); });
  }, [isLoggedIn]);

  // ⚡ Bolt: Added debounce to search query to prevent frequent O(N) filtering
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Filtered + searched video list
  const filteredVideos = useMemo(() => {
    const source     = videosByCategory[activeCat] || allVideos;
    const q          = debouncedSearchQuery.toLowerCase();
    const blockedSet = new Set(Object.keys(blockedChannels));

    // ⚡ Bolt: Replaced .filter() with native for loop and cached strings with WeakMap
    const result = [];
    for (let i = 0; i < source.length; i++) {
      const v = source[i];

      if (q) {
        let t = titleCache.get(v);
        if (t === undefined) {
          t = (v.snippet?.title || '').toLowerCase();
          titleCache.set(v, t);
        }
        let ch = channelTitleCache.get(v);
        if (ch === undefined) {
          ch = (v.snippet?.channelTitle || '').toLowerCase();
          channelTitleCache.set(v, ch);
        }
        if (!t.includes(q) && !ch.includes(q)) continue;
      }

      const chId      = v.snippet?.channelId || v.snippet?.channelid || '';
      const isFlagged = !!flaggedVideos[v.id];
      const isBlocked = blockedSet.has(chId);

      if (showFilter === 'flagged' && !(isFlagged || isBlocked)) continue;
      if (showFilter === 'clean' && (isFlagged || isBlocked)) continue;

      result.push(v);
    }
    return result;
  }, [videosByCategory, allVideos, activeCat, debouncedSearchQuery, blockedChannels, flaggedVideos, showFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / pageSize));
  const pageVideos = filteredVideos.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // ⚡ Bolt: Stabilized callbacks to prevent invalidating React.memo children
  // Flag toggle (optimistic)
  const toggleFlag = useCallback(async (videoId, def, priorFlags) => {
    const prior  = priorFlags || {};
    const isOn   = prior[def.key] === def.activeVal;
    const newVal = isOn ? def.inactiveVal : def.activeVal;

    const updated = { ...prior, [def.key]: newVal };
    Object.keys(updated).forEach(k => { if (updated[k] === FLAG_DEFAULTS[k]) delete updated[k]; });

    setFlaggedVideos(prev => {
      const next = { ...prev };
      if (Object.keys(updated).length === 0) delete next[videoId];
      else next[videoId] = updated;
      return next;
    });

    try {
      await apiFetch(token, 'POST', { action: 'flag_video', id: videoId, flags: updated });
      addToast(isOn ? 'Flag removed' : 'Flag set ✓');
    } catch (e) {
      setFlaggedVideos(prev => {
        const reverted = { ...prev };
        if (Object.keys(prior).length === 0) delete reverted[videoId];
        else reverted[videoId] = prior;
        return reverted;
      });
      addToast('Save failed: ' + e.message, 'error');
    }
  }, [token, addToast]);

  // Channel block toggle (optimistic)
  const toggleChannelBlock = useCallback(async (chId, chTitle, priorInfo) => {
    if (!chId) { addToast('No channel ID for this video', 'error'); return; }
    const isBlocked = !!priorInfo;

    setBlockedChannels(prev => {
      const next = { ...prev };
      if (isBlocked) delete next[chId];
      else next[chId] = { title: chTitle, blocked_at: new Date().toISOString().slice(0, 10) };
      return next;
    });

    try {
      if (isBlocked) {
        await apiFetch(token, 'POST', { action: 'unblock_channel', channel_id: chId });
        addToast(`Unblocked: ${chTitle}`);
      } else {
        await apiFetch(token, 'POST', { action: 'block_channel', channel_id: chId, channel_title: chTitle });
        addToast(`Blocked: ${chTitle}`);
      }
    } catch (e) {
      setBlockedChannels(prev => {
        const reverted = { ...prev };
        if (isBlocked) reverted[chId] = priorInfo;
        else delete reverted[chId];
        return reverted;
      });
      addToast('Save failed: ' + e.message, 'error');
    }
  }, [token, addToast]);

  // ── Render: login gate ─────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <div className="ar-root">
        <div className="ar-login-overlay">
          <div className="ar-login-box">
            <h2>🛡 Admin Login</h2>
            <p>ASMR.guru video review panel</p>
            <input
              type="password"
              placeholder="Admin password"
              value={loginPw}
              autoComplete="current-password"
              onChange={e => setLoginPw(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doLogin(loginPw)}
            />
            {loginError && <div className="ar-login-error">Incorrect password</div>}
            <button className="ar-btn-primary" onClick={() => doLogin(loginPw)}>Sign in</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: admin panel ────────────────────────────────────────

  return (
    <div className="ar-root">

      {/* Top bar */}
      <div className="ar-topbar">
        <h1>ASMR<span>.guru</span> Admin</h1>
        <div className="ar-viewswitch">
          <button
            className={`ar-btn-sm${adminView === 'videos' ? ' ar-active' : ''}`}
            onClick={() => setAdminView('videos')}
          >
            Video Review
          </button>
          <button
            className={`ar-btn-sm${adminView === 'gear' ? ' ar-active' : ''}`}
            onClick={() => setAdminView('gear')}
          >
            Gear
          </button>
        </div>

        {adminView === 'videos' && (
          <>
            <div className="ar-stats-bar">
              <span className="ar-stat">Videos: <strong>{allVideos.length.toLocaleString()}</strong></span>
              <span className="ar-stat">Flagged: <strong>{Object.keys(flaggedVideos).length}</strong></span>
              <span className="ar-stat">Blocked ch.: <strong>{Object.keys(blockedChannels).length}</strong></span>
            </div>
            <input
              type="search"
              className="ar-search"
              placeholder="Search title or channel…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
            {['all', 'flagged', 'clean'].map(f => (
              <button
                key={f}
                className={`ar-btn-sm${showFilter === f ? ' ar-active' : ''}`}
                onClick={() => { setShowFilter(f); setCurrentPage(1); }}
              >
                {f === 'all' ? 'All' : f === 'flagged' ? 'Flagged' : 'Clean'}
              </button>
            ))}
          </>
        )}
      </div>

      {adminView === 'videos' && (
      <>
      {/* Filter bar */}
      <div className="ar-filter-bar">
        <label>Category:</label>
        <select
          className="ar-select"
          value={activeCat}
          onChange={e => { setActiveCat(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All videos</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ marginLeft: 12 }}>Per page:</label>
        <select
          className="ar-select"
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
        >
          {[24, 48, 96].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {/* Main layout */}
      <div className="ar-layout">
        <div className="ar-main">
          {loading   && <div className="ar-loading"><div className="ar-spinner" />Loading videos…</div>}
          {loadError && <div className="ar-error">Failed to load: {loadError}</div>}

          {!loading && !loadError && (
            <>
              <div className="ar-grid">
                {pageVideos.length === 0
                  ? <div className="ar-empty">No videos match the current filter.</div>
                  : pageVideos.map(v => (
                    <VideoCard
                      key={v.id}
                      video={v}
                      flags={flaggedVideos[v.id] || EMPTY_FLAGS}
                      blockedInfo={blockedChannels[v.snippet?.channelId || v.snippet?.channelid || '']}
                      onFlagToggle={toggleFlag}
                      onChannelBlock={toggleChannelBlock}
                    />
                  ))
                }
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                total={filteredVideos.length}
                pageSize={pageSize}
                onPageChange={p => { setCurrentPage(p); window.scrollTo(0, 0); }}
              />
            </>
          )}
        </div>

        {/* Sidebar: blocked channels + legend */}
        <div className="ar-sidebar">
          <h3>Blocked Channels</h3>
          <BlockedList blockedChannels={blockedChannels} onUnblock={toggleChannelBlock} />
          <div className="ar-legend">
            <h3>Flag legend</h3>
            {[
              { color: '#e05555', label: 'Inappropriate / Naughty' },
              { color: '#e07a30', label: 'Not ASMR content' },
              { color: '#555e7a', label: 'Not embeddable' },
              { color: '#4a90d9', label: 'Made for Kids (YT flag)' },
              { color: '#30a09a', label: 'Self-declared Kids' },
            ].map(({ color, label }) => (
              <div key={label} className="ar-legend-row">
                <div className="ar-legend-dot" style={{ background: color }} />
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      )}

      {adminView === 'gear' && (
        <GearManager token={token} addToast={addToast} />
      )}

      {/* Toast notifications */}
      <div className="ar-toasts">
        {toasts.map(t => (
          <div key={t.id} className={`ar-toast ar-toast-${t.type}`}>{t.msg}</div>
        ))}
      </div>
    </div>
  );
}
